/******************************************
 *  Author : vsuthar
 *  Created On : Thu Sep 02 2021
 *  File : GlobalModule.js
 *  ©Palo Alto Networks
 *******************************************/

import { Tab, TabList, TabPanel } from '@panwds/react-ui-legacy'
import ModalProviders from './common/providers/ModalProviders'
import { getFrameworkVar, history, getTsgAccess } from '@sparky/framework'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import Drawer from './common/components/drawer/Drawer'
import SelectTab from './common/components/tabs/SelectTab'
import ZIndex from './common/components/zIndex/ZIndex'
import {
  VIEW_ADD_CUSTOM_ROLE,
  VIEW_EDIT_CUSTOM_ROLE,
  VIEW_IAM_PROVIDERS,
  VIEW_IAM_ROLES,
  VIEW_MANAGE_IAM,
  VIEW_MANAGE_SUBSCRIPTION,
  VIEW_MANAGE_TENANT,
  VIEW_TENANT_SWITCHER,
  VIEW_DEVICE_ASSOCIATIONS,
  SUBSCRIPTION_PERMISSION_SET,
  TENANCY_PERMISSION_SET,
  IAM_PERMISSION_SET,
  DEVICES_PERMISSION_SET,
  IP_ALLOW_PERMISSION_SET,
  TSG_NONE,
  VIEW_IP_ALLOW_LIST,
} from './common/constants/GlobalConstants'
import {
  IAM_SELECT_MANAGEMENT,
  IAM_SELECT_FEDERATIONS,
  IAM_SELECT_ROLES,
  TAB_DEVICE_ASSOCIATIONS,
  TAB_IAM_MANAGEMENT,
  TAB_IAM_ROLES,
  TAB_SUBSCRIPTIONS_MANAGEMENT,
  TAB_TENANT_MANAGEMENT,
  TAB_IAM_FEDERATIONS,
  TAB_IP_ALLOW
} from './common/constants/LabelConstants'
import { selectTreeNode } from './common/reducers/actions/TreeReducer.actions'
import { getVarValue, setCurrentViewing, setIsGlobalModuleOpen } from './common/utils/Sparky'
import './globalModule.scss'
import IAM from './iam'
import Subscription from './subscription'
import Tenancy from './tenancy'
import IpAllowList from './ipAllowList'
import {
  LABEL_COMMON_SERVICES_SUB_TITLE,
  LABEL_SUBSCRIPTIONS_AND_TENANTS
} from './tenancy/constants/LabelConstants'
import {
  FF_DAS_ENABLED,
  FF_PROVIDERS_ENABLED,
  FF_ROLES_ENABLED,
  FF_SHOW_TENANCY,
  FF_SHOW_IAM,
  FF_COMMON_SERVICES_CUSTOM_ROLES,
  FF_IP_ALLOW_LIST,
  FF_FEDRAMP_HIGH,
  canUseIpAllow
} from './common/utils/FeatureFlags'
import Devices from './devices'
import MessageProvider from './common/providers/MessageProvider'
import { isAccountAllowedForDAS } from './common/utils/Utils'
import GlobalContext from './common/context/GlobalContext'
import NoAccess from './common/components/noAccess'
import { IDENTITIES_PERMISSION_SET, IDP_PERMISSION_SET, ROLES_PERMISSION_SET } from './iam/constants/IAMConstants'

const rolesEnabled = getVarValue(FF_ROLES_ENABLED, false)
const providersEnabled = getVarValue(FF_PROVIDERS_ENABLED, false)
const dasEnabledFF = getVarValue(FF_DAS_ENABLED, false) && getFrameworkVar(FF_DAS_ENABLED) !== false
const tenancyEnabled = getVarValue(FF_SHOW_TENANCY, true)
const iamEnabled = getVarValue(FF_SHOW_IAM, true)
const customRolesEnabled = getVarValue(FF_COMMON_SERVICES_CUSTOM_ROLES, true)
const ipAllowEnabled = getVarValue(FF_IP_ALLOW_LIST, false)
const fedHighEnv = getVarValue(FF_FEDRAMP_HIGH, false)

const GlobalModule = (props) => {
  const {
    dispatch,
    globalDispatch,
    commonModule,
    showActionStatus,
    auth,
    sidecar,
    ...rest
  } = props
  const contextTsgID = auth?.tsg_id || ''
  const currentView = commonModule?.path || ''
  const isGlobalModuleOpen = commonModule?.path !== VIEW_TENANT_SWITCHER && Boolean(commonModule?.visible)
  const dasEnabled = dasEnabledFF && isAccountAllowedForDAS(props?.apps, auth?.user)

  /**
  * =========== All local states ==============
  */
  const { treeReducer } = useContext(GlobalContext)
  const [subscriptionMenuVisible, setSubscriptionMenuVisible] = useState(false)
  const [tenantManagementMenuVisible, setTenantManagementMenuVisible] = useState(false)
  const [iamMenuVisible, setIamMenuVisible] = useState(false)
  const [accessMenuVisible, setAccessMenuVisible] = useState(false)
  const [rolesMenuVisible, setRolesMenuVisible] = useState(false)
  const [federationsMenuVisible, setFederationsMenuVisible] = useState(false)
  const [ipAllowVisible, setIpAllowVisible] = useState(false)
  const [deviceMenuVisible, setDeviceMenuVisible] = useState(false)
  const [iamTab, setIAMTab] = useState(VIEW_MANAGE_IAM)
  const { selectedTreeNode } = treeReducer

  useEffect(() => {
    if (currentView.startsWith(VIEW_MANAGE_IAM)) {
      setIAMTab(VIEW_MANAGE_IAM)
    }
    else if (currentView.startsWith(VIEW_IAM_ROLES)) {
      setIAMTab(VIEW_IAM_ROLES)
    }
    else if (currentView.startsWith(VIEW_IAM_PROVIDERS)) {
      setIAMTab(VIEW_IAM_PROVIDERS)
    }
  }, [currentView])

  /**
  * =========== All callback ==============
  */
  const setActiveTabId = useCallback((tabId) => {
    setCurrentViewing(tabId)
  }, [])

  const removedUserFLow = () => {
    //sessionStorage.removeItem(USER_FLOW)
  }

  useEffect(() => {
    window.addEventListener('onbeforeunload', removedUserFLow)
    return () => window.removeEventListener('onbeforeunload', removedUserFLow)
  }, [])

  /**
    * Callback to open close drawer
    */
  const handleGlobalModuleHideShow = useCallback(() => {
    //sessionStorage.removeItem(USER_FLOW)
    if (contextTsgID === TSG_NONE.id || !Boolean(contextTsgID)) {
      return
    }
    // return to default route if user close GlobalModule from /setup route
    const currentPath = history.location.pathname
    if (currentPath.startsWith('/setup')) {
      history.push('/')
    }
    else if (currentPath.startsWith('/hub/setup')) {
      history.push('/hub')
    }
    setIsGlobalModuleOpen(false)
  }, [contextTsgID])

  const onBreadCrumbClick = useCallback((obj) => {
    dispatch(selectTreeNode(obj))
  }, [dispatch])

  const onIAMSelectChange = useCallback(({ selectedItem, ...rest }) => {
    setCurrentViewing(selectedItem.view)
  }, [])


  /**
   * APPORTAL-6389
   * This is control tab visible or not when customer comes via day 0
   */
  const allowVisibleTab = useMemo(() => {
    return contextTsgID !== TSG_NONE.id || !Boolean(contextTsgID)
  }, [contextTsgID])

  useEffect(() => {
    let mounted = true
    const fetchMenuVisible = async () => {
      if (selectedTreeNode?.id) {
        const visibleObj = await getTsgAccess(selectedTreeNode.id).then((tsgAccess) => {
          return {
            subscription: tsgAccess.hasPermissionSetChildren(SUBSCRIPTION_PERMISSION_SET) || !customRolesEnabled,
            tenancy: tsgAccess.hasPermissionSetChildren(TENANCY_PERMISSION_SET) || !customRolesEnabled,
            iam: tsgAccess.hasPermissionSetChildren(IAM_PERMISSION_SET) || !customRolesEnabled,
            access: tsgAccess.hasPermissionSetChildren(IDENTITIES_PERMISSION_SET) || !customRolesEnabled,
            roles: tsgAccess.hasPermissionSetChildren(ROLES_PERMISSION_SET) || !customRolesEnabled,
            federations: tsgAccess.hasPermissionSetChildren(IDP_PERMISSION_SET) || !customRolesEnabled,
            das: tsgAccess.hasPermissionSetChildren(DEVICES_PERMISSION_SET) || !customRolesEnabled,
            ipAllow: (canUseIpAllow(selectedTreeNode) && tsgAccess.hasPermissionSetChildren(IP_ALLOW_PERMISSION_SET)) || !customRolesEnabled
          }
        })
        if (mounted) {
          setSubscriptionMenuVisible(visibleObj.subscription)
          setTenantManagementMenuVisible(visibleObj.tenancy)
          setIamMenuVisible(visibleObj.iam)
          setAccessMenuVisible(visibleObj.access)
          setRolesMenuVisible(visibleObj.roles)
          setFederationsMenuVisible(visibleObj.federations)
          setDeviceMenuVisible(visibleObj.das)
          setIpAllowVisible(visibleObj.ipAllow)
        }
      }
      else {
        if (mounted) {
          // for day 0, fed high env is using old activation flow which requires subscription tab to be visible
          setSubscriptionMenuVisible(false || fedHighEnv)
          setTenantManagementMenuVisible(false)
          setIamMenuVisible(false)
          setDeviceMenuVisible(false)
          setIpAllowVisible(false)
        }
      }
    }
    fetchMenuVisible()
    return () => {
      mounted = false
    }
  }, [selectedTreeNode])

  const getCurrentView = useMemo(() => {
    if (currentView.startsWith(VIEW_MANAGE_TENANT)) {
      return VIEW_MANAGE_TENANT
    }
    else if (currentView.startsWith(VIEW_MANAGE_IAM)) {
      return VIEW_MANAGE_IAM
    }
    else if (currentView.startsWith(VIEW_ADD_CUSTOM_ROLE)) {
      return VIEW_IAM_ROLES
    }
    else if (currentView.startsWith(VIEW_EDIT_CUSTOM_ROLE)) {
      return VIEW_IAM_ROLES
    }
    else if (currentView.startsWith(VIEW_IAM_ROLES)) {
      return VIEW_IAM_ROLES
    }
    else if (currentView.startsWith(VIEW_IAM_PROVIDERS)) {
      return VIEW_IAM_PROVIDERS
    }
    else if (currentView.startsWith(VIEW_MANAGE_SUBSCRIPTION)) {
      return VIEW_MANAGE_SUBSCRIPTION
    }
    else if (currentView.startsWith(VIEW_DEVICE_ASSOCIATIONS) && dasEnabled) {
      return VIEW_DEVICE_ASSOCIATIONS
    }
    else if (currentView.startsWith(VIEW_IP_ALLOW_LIST) && ipAllowEnabled) {
      return VIEW_IP_ALLOW_LIST
    }
    else {
      return VIEW_MANAGE_TENANT
    }
  }, [currentView, dasEnabled])

  const iamOptions = useMemo(() => {
    const options = [
    ]
    if (accessMenuVisible) {
      options.push({ value: IAM_SELECT_MANAGEMENT, view: VIEW_MANAGE_IAM })
    }
    if (rolesEnabled && rolesMenuVisible) {
      options.push({ value: IAM_SELECT_ROLES, view: VIEW_IAM_ROLES })
    }
    if (providersEnabled && federationsMenuVisible) {
      options.push({ value: IAM_SELECT_FEDERATIONS, view: VIEW_IAM_PROVIDERS })
    }
    return options
  }, [accessMenuVisible, federationsMenuVisible, rolesMenuVisible])

  const iamTabName = useMemo(() => {
    switch (iamTab) {
      case VIEW_IAM_ROLES:
        return TAB_IAM_ROLES
      case VIEW_IAM_PROVIDERS:
        return TAB_IAM_FEDERATIONS
      case VIEW_IP_ALLOW_LIST:
        return TAB_IP_ALLOW
      case VIEW_MANAGE_IAM:
      default:
        return TAB_IAM_MANAGEMENT
    }
  }, [iamTab])

  const tabs = useMemo(() => {
    return <>
      <div className={'panwds-tw2 panwds-unset-reboot cm-relative cm-bg-gray-100 dark:cm-bg-gray-1050 tab-bar-wrapper cm-flex-1'}>
        <div className='cm-flex-row cm-flex cm-justify-between cmm-legacy-tabs'>
          <TabList activeTabId={getCurrentView}
            onActiveChange={setActiveTabId}>
            {subscriptionMenuVisible && rest.showSubscriptions && <Tab style={{ borderWidth: 3 }} id={VIEW_MANAGE_SUBSCRIPTION}>{TAB_SUBSCRIPTIONS_MANAGEMENT}</Tab>}
            {tenantManagementMenuVisible && (tenancyEnabled && allowVisibleTab) && <Tab style={{ borderWidth: 3 }} id={VIEW_MANAGE_TENANT}>{TAB_TENANT_MANAGEMENT}</Tab>}
            {iamMenuVisible && (allowVisibleTab ? (rolesEnabled || providersEnabled) ? (
              <SelectTab
                id={iamTab}
                items={iamOptions}
                onChange={onIAMSelectChange}
              >{iamTabName}</SelectTab>
            ) : (<Tab style={{ borderWidth: 3 }} id={VIEW_MANAGE_IAM}>{TAB_IAM_MANAGEMENT}</Tab>) : null)}
            {deviceMenuVisible && dasEnabled && allowVisibleTab && (
              <Tab id={VIEW_DEVICE_ASSOCIATIONS} style={{ borderWidth: 3 }}>{TAB_DEVICE_ASSOCIATIONS}</Tab>
            )}
            {ipAllowEnabled && ipAllowVisible && allowVisibleTab && (
              <Tab id={VIEW_IP_ALLOW_LIST} style={{ borderWidth: 3 }}>{TAB_IP_ALLOW}</Tab>
            )}
          </TabList>
        </div>
      </div>
    </>
  }, [getCurrentView, setActiveTabId, subscriptionMenuVisible, rest.showSubscriptions, tenantManagementMenuVisible, allowVisibleTab, iamMenuVisible, iamTab, iamOptions, onIAMSelectChange, iamTabName, deviceMenuVisible, dasEnabled, ipAllowVisible])

  const child = useMemo(() => {
    if (!subscriptionMenuVisible && !tenantManagementMenuVisible && !iamMenuVisible && !deviceMenuVisible) {
      return <div className='tab-container panwds-tw3 cm-w-full cm-h-full cm-bg-white cm-relative dark:cm-bg-gray-1050'>
        <NoAccess { ...{ ...props } }/>
      </div>
    }
    return (
      <MessageProvider>
        <TabPanel addClassName='cm-w-full cm-h-full' activeTabId={getCurrentView} forTabId={VIEW_MANAGE_SUBSCRIPTION}>
          <div className='subscription-tab-container panwds-tw3 cm-bg-white cm-relative cm-h-full dark:cm-bg-gray-1050'>
            <Subscription {...{ auth, ...props }} />
          </div>
        </TabPanel>
        {tenancyEnabled && allowVisibleTab && (
          <TabPanel addClassName='cm-w-full cm-h-full' activeTabId={getCurrentView} forTabId={VIEW_MANAGE_TENANT}>
            <div className='tab-container panwds-tw3 cm-w-full cm-h-full cm-bg-white cm-relative dark:cm-bg-gray-1050'>
              <Tenancy
                globalDispatch={globalDispatch}
                dispatch={dispatch}
                currentView={currentView}
                onBreadCrumbClick={onBreadCrumbClick} {...{ auth, ...rest, ...props }}
              />
            </div>
          </TabPanel>
        )}
        {iamEnabled && allowVisibleTab && (
          <TabPanel addClassName='cm-w-full cm-h-full cm-bg-white' activeTabId={getCurrentView} forTabId={iamTab}>
            <div className='tab-container panwds-tw3 cm-w-full cm-h-full cm-bg-white cm-relative dark:cm-bg-gray-1050'>
              <IAM {...{ auth, currentView, dispatch, onBreadCrumbClick, globalDispatch, ...rest }} />
            </div>
          </TabPanel>
        )}
        {dasEnabled && allowVisibleTab && (
          <TabPanel addClassName='cm-w-full cm-h-full' forTabId={VIEW_DEVICE_ASSOCIATIONS} activeTabId={getCurrentView}>
            <div className='tab-container panwds-tw3 cm-w-full cm-h-full cm-bg-white cm-relative dark:cm-bg-gray-1050'>
              <Devices {...{ auth, ...props, onBreadCrumbClick }} />
            </div>
          </TabPanel>
        )}
        {ipAllowEnabled && allowVisibleTab && (
          <TabPanel addClassName='cm-w-full cm-h-full' forTabId={VIEW_IP_ALLOW_LIST} activeTabId={getCurrentView}>
            <div className='tab-container panwds-tw3 cm-w-full cm-h-full cm-bg-white cm-relative dark:cm-bg-gray-1050'>
              <div className='cm-absolute cm-inset-0'>
                <IpAllowList />
              </div>
            </div>
          </TabPanel>
        )}
      </MessageProvider>
    )
  }, [subscriptionMenuVisible, tenantManagementMenuVisible, iamMenuVisible, deviceMenuVisible, getCurrentView, auth, props, allowVisibleTab, globalDispatch, dispatch, currentView, onBreadCrumbClick, rest, iamTab, dasEnabled])

  return (
    <ModalProviders>
      <ZIndex>
        <Drawer
          isOpen={isGlobalModuleOpen}
          pageHeaderProps={{ className: 'cm-p-3 cm-bg-gray-100 dark:cm-bg-gray-1050', level: 2, size: 'md' }}
          subTitle={LABEL_COMMON_SERVICES_SUB_TITLE}
          title={LABEL_SUBSCRIPTIONS_AND_TENANTS}
          actionStatus={showActionStatus}
          tabs={tabs}
          content={child}
          showCloseButton={true}
          onClose={handleGlobalModuleHideShow}
          {...{ sidecar, dispatch, ...rest }}
        >
        </Drawer>
      </ZIndex>
    </ModalProviders>
  )
}
export default GlobalModule
